import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef } from 'react'
import {
    FileMetaDataEntities,
    InvoiceListItemDto,
    InvoiceMutationDto,
    InvoiceStatus,
    InvoiceTemplateDto,
    InvoiceType,
    LineItemGroupMutationDto,
    LineItemGroupType,
    LineItemMutationDto,
    LineItemType,
    PrefillDeterminer,
    PrefillPeriod,
} from '../../../../config/finance/invoiceConfig'
import { useFormikContext } from 'formik'
import { defaultInvoiceValues } from '../../../../pages/Finance/Invoice/AddInvoice'
import { InvoiceProps } from './InvoiceForm'
import * as UUID from 'uuid'
import { PricePrecedence } from '../../../../config/finance/financeConfig'
import { LineItemEntities, LineItemEntity } from '../../../../hooks/finance/invoice/useLineItemEntities'
import { DateTime, DateTimeUnit, Interval } from 'luxon'
import { useTranslation } from 'react-i18next'
import FilePicker from '../../../TagPicker/FilePicker'
import { Field, makeStyles, shorthands, tokens } from '@fluentui/react-components'
import BaseCombobox from '../../../Combobox/BaseCombobox'
import { usePrevious } from '@fluentui/react-hooks'
import { sortLineItemGroups } from '../../../../pages/Finance/Invoice/EditInvoice'
import { FixedPriceListItemDto } from '../../../../config/registrationConfig'

const useStyles = makeStyles({
    formRow: {
        display: 'grid',
        gridTemplateColumns: '34% 66%',
        ...shorthands.gap(tokens.spacingVerticalL),
        marginRight: tokens.spacingVerticalL,
    },
})

const PeriodKeyMap = new Map<PrefillPeriod, DateTimeUnit>([
    [PrefillPeriod.Year, 'year'],
    [PrefillPeriod.Month, 'month'],
    [PrefillPeriod.Week, 'week'],
])

export function getFilterInterval(period: PrefillPeriod, offset: PrefillDeterminer, cutoff: PrefillDeterminer): Interval {
    const now = DateTime.local()
    const periodKey = PeriodKeyMap.get(period)

    const getDeterminerValue = (determiner: PrefillDeterminer, target: DateTime | false, boundary: DateTime) => {
        if (!target) {
            return boundary
        }
        switch (determiner) {
            case PrefillDeterminer.Previous:
                return target.minus({ [periodKey]: 1 })
            case PrefillDeterminer.CurrentIfMost:
                const twoThirds = now.startOf(periodKey).plus({ [periodKey]: 2 / 3 })
                return now >= twoThirds ? target : target.minus({ [periodKey]: 1 })
            case PrefillDeterminer.Current:
                return target
            case PrefillDeterminer.All:
                return boundary
        }
    }

    // If no period key has been set, we'll default to the boundary, which is every date with a 4-digit year.
    // Default period key is year, because the size of the boundary is exactly 10.000 years.
    return Interval.fromDateTimes(
        getDeterminerValue(offset, periodKey && now, DateTime.fromISO('0000-01-01')).startOf(periodKey || 'year'),
        getDeterminerValue(cutoff, periodKey && now, DateTime.local().endOf('year')).endOf(periodKey || 'year'),
    )
}

export function getTemplateInterval(templates?: InvoiceTemplateDto[]): Interval {
    return (
        templates
            ?.filter(template => template.isDefault)
            .flatMap(template => template.lineItemGroups)
            .filter(group => group.prefillCutoff)
            .map(group =>
                Interval.fromDateTimes(
                    getFilterInterval(group.prefillPeriod, group.prefillOffset, group.prefillCutoff).start,
                    getFilterInterval(group.prefillPeriod, group.prefillOffset, group.prefillCutoff).end,
                ),
            )
            .find(interval => interval.isValid) ||
        Interval.fromDateTimes(DateTime.local().minus({ months: 12 }).startOf('month'), DateTime.local().minus({ months: 1 }).endOf('month'))
    )
}

export const filterEntities = (
    entities: LineItemEntity[],
    period: PrefillPeriod,
    offset: PrefillDeterminer,
    cutoff: PrefillDeterminer,
): LineItemEntity[] => {
    const interval = getFilterInterval(period, offset, cutoff)
    return (entities || []).filter(entity => interval.contains(DateTime.fromISO(entity.date)))
}

const filterEmpty = obj =>
    Object.fromEntries(
        Object.entries(obj || {})
            .filter(([, value]) => ![undefined, null].includes(value))
            .map(([key, value]) => [key, typeof value === 'object' && !Array.isArray(value) ? filterEmpty(value) : value]),
    )

export const mapLineItemGroupTemplate = (
    file: FileMetaDataEntities,
    template: LineItemGroupType,
    fixedPrice: FixedPriceListItemDto,
    entities?: LineItemEntities,
): LineItemGroupMutationDto => {
    // If we have a fixed price but the prefill type doesn't match then don't render the group.
    if (fixedPrice && template.prefillType && !fixedPrice.type?.prefillType?.includes(template.prefillType)) {
        return undefined
    }

    const lineItems = template.prefillType && entities ? mapLineItems(file.file.id, template, entities) : []
    const precedence = fixedPrice ? PricePrecedence.Subtotal : (template.precedence ?? PricePrecedence.Inherited)

    // Only create a line item group when we need to prefill, but it doesn't contain any of the prefill items.
    if (template.prefillType && lineItems.length === 0 && precedence === PricePrecedence.Inherited) {
        return undefined
    }

    return {
        id: UUID.v4(),
        templateGroupId: template.id,
        fileId: file.file.id,
        fixedPriceId: fixedPrice?.id,
        file: { ...file.file, ownerId: file.file.owner.id },
        precedence,
        lineItems,
        description: fixedPrice?.description ?? template.description,
        price: fixedPrice?.rate?.amount ?? template.price,
        order: template.order || 10,
        vatType: fixedPrice?.vatType ?? template.vatType,
        showStatement: fixedPrice?.type?.showStatement ?? template.showStatement,
        consolidation: template.consolidation,
        accounting: template.accounting,
    }
}

export const mapLineItems = (fileId: string, template: LineItemGroupType, entities: LineItemEntities): LineItemMutationDto[] =>
    filterEntities(
        [...(entities.orphans.get(template.prefillType) || []), ...(entities.selection.get(template.prefillType) || [])],
        template.prefillPeriod,
        template.prefillOffset,
        template.prefillCutoff,
    )
        .filter(
            entity =>
                template.prefillType !== LineItemType.Invoice ||
                // Only ever prefill deposit invoices.
                (entity as InvoiceListItemDto).type === InvoiceType.Deposit,
        )
        .filter(entity => entity.file.id === fileId)
        .map(entities.map)
        .filter(Boolean)

export const mapInvoiceTemplate = (
    files: FileMetaDataEntities[],
    template: InvoiceTemplateDto,
    values: Partial<InvoiceMutationDto>,
    lineItemGroupsOverrides: LineItemGroupMutationDto[],
    entities?: LineItemEntities,
): InvoiceMutationDto => {
    return {
        ...filterEmpty(defaultInvoiceValues()),
        ...filterEmpty(values),
        ...filterEmpty(template),
        id: undefined,
        name: values?.name,
        fileIds: values?.fileIds,
        lineItemGroups:
            lineItemGroupsOverrides.length > 0
                ? lineItemGroupsOverrides
                : [
                      ...files
                          ?.flatMap(file =>
                              template?.lineItemGroups?.map(groupTemplate =>
                                  mapLineItemGroupTemplate(
                                      file,
                                      groupTemplate,
                                      filterEntities(
                                          [
                                              ...(entities.orphans.get(LineItemType.FixedPrice) || []),
                                              ...(entities.selection.get(LineItemType.FixedPrice) || []),
                                          ],
                                          groupTemplate.prefillPeriod,
                                          groupTemplate.prefillOffset,
                                          groupTemplate.prefillCutoff,
                                      )
                                          .map(entity => file.fixedPrices.find(item => item.id === entity.id))
                                          .find(entity => entity?.type?.prefillType?.includes(groupTemplate.prefillType)),
                                      entities,
                                  ),
                              ),
                          )
                          .filter(Boolean)
                          .sort(sortLineItemGroups),

                      ...files?.flatMap(file =>
                          filterEntities(
                              [...(entities.orphans.get(LineItemType.FixedPrice) || []), ...(entities.selection.get(LineItemType.FixedPrice) || [])],
                              PrefillPeriod.Year,
                              PrefillDeterminer.All,
                              PrefillDeterminer.All,
                          )
                              .map(entity => file.fixedPrices.find(item => item.id === entity.id))
                              .filter(entity => entity?.type?.prefillType && entity?.type?.prefillType.length === 0)
                              .map(entity => mapLineItemGroupTemplate(file, { id: UUID.v4() }, entity, entities)),
                      ),
                  ]
                      .filter(Boolean)
                      .sort(sortLineItemGroups) || defaultInvoiceValues().lineItemGroups,
    }
}

export default function InvoiceFile({ useInitialInvoice, useInitialValues, templates, meta, lineItemEntities }: InvoiceProps): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const { initialValues, values, setValues, setFieldValue } = useFormikContext<InvoiceMutationDto>()
    const previousValues = usePrevious(values)

    const fileRef = useRef<HTMLDivElement>()

    const hasFileIdsChanged = useMemo(() => {
        return (
            values.fileIds?.length > 0 &&
            (values.fileIds?.length !== previousValues?.fileIds?.length || values.fileIds?.some(id => !previousValues?.fileIds?.includes(id)))
        )
    }, [values.fileIds])

    useEffect(() => {
        if (!values.templateId && !useInitialInvoice) {
            setFieldValue('templateId', templates?.find(template => template.isDefault)?.id)
        }
    }, [])

    useEffect(() => {
        if (values.templateId && meta.loaded) {
            if (useInitialInvoice) {
                setValues(initialValues)
                return
            }
            const newValues = mapInvoiceTemplate(
                meta?.data?.files,
                templates?.find(template => values.templateId === template.id),
                values,
                useInitialValues ? initialValues.lineItemGroups : [],
                lineItemEntities,
            )
            setValues(newValues)
        }
    }, [values.templateId, meta?.data?.files, hasFileIdsChanged])

    return (
        <div className={styles.formRow}>
            {templates?.length > 0 && (
                <Field label={translate('invoice.templateid.label')} orientation={'vertical'} required>
                    <BaseCombobox<InvoiceTemplateDto>
                        required
                        entities={templates}
                        getEntityLabel={entity => entity.name}
                        getEntityValue={entity => entity.id}
                        placeholderKey={'invoice.templateid.placeholder'}
                        selectedOptions={[values.templateId]}
                        onOptionSelect={(_, data) => setFieldValue('templateId', data.selectedOptions?.[0])}
                        disabled={values.status !== InvoiceStatus.New}
                    />
                </Field>
            )}
            <Field label={translate('invoice.fileid.label')} orientation={'vertical'} required>
                <FilePicker
                    autoFocus
                    selectedOptions={values.fileIds ?? []}
                    onOptionsSelect={(_, data) => {
                        setFieldValue('fileIds', data)
                        fileRef.current?.blur()
                    }}
                    disabled={values.status !== InvoiceStatus.New}
                />
            </Field>
        </div>
    )
}
